import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';
import { useIntl } from 'react-intl';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { disableNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/disable-native-drag-preview';
import { preventUnhandled } from '@atlaskit/pragmatic-drag-and-drop/prevent-unhandled';
import {
  getProposedPanelWidth,
  useSplitScreenResize,
  useSplitScreenSharedState,
  type PanelNames,
} from '@trello/split-screen';
import type { SplitScreenTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import styles from './CollapsibleSplitScreenResizeDivider.less';

interface CollapsibleSplitScreenResizeDividerProps {
  shouldHideDivider: boolean;
  leftPanelName: PanelNames;
  rightPanelName: PanelNames;
  leftPreviousWidth: number;
  rightPreviousWidth: number;
  handleSetWidthCallback: (panelName: PanelNames, width: number) => void;
  handlePanelCollapseCallback: (
    panelName: PanelNames,
    isCollapsed: boolean,
  ) => void;
  handleDoubleClick: () => void;
  enableTransitionsCallback: (enable: boolean) => void;
}

export const CollapsibleSplitScreenResizeDivider: FunctionComponent<
  CollapsibleSplitScreenResizeDividerProps
> = ({
  shouldHideDivider,
  leftPanelName,
  rightPanelName,
  leftPreviousWidth,
  rightPreviousWidth,
  handleSetWidthCallback,
  handlePanelCollapseCallback,
  handleDoubleClick,
  enableTransitionsCallback,
}) => {
  const intl = useIntl();
  const dividerRef = useRef<HTMLDivElement>(null);

  const { getMaxWidth, getMinWidth, collapsePanel } = useSplitScreenResize();

  const { resizeAllPanelsToDefault, updateResizedPanelWidth } =
    useSplitScreenSharedState();

  const leftMaxWidth = getMaxWidth(leftPanelName);
  const leftMinWidth = getMinWidth(leftPanelName);
  const rightMaxWidth = getMaxWidth(rightPanelName);
  const rightMinWidth = getMinWidth(rightPanelName);
  const [isDragging, setIsDragging] = useState(false);

  const [leftPanelPreviousWidth, setLeftPanelPreviousWidth] =
    useState(leftPreviousWidth);
  const [rightPanelPreviousWidth, setRightPanelPreviousWidth] =
    useState(rightPreviousWidth);

  const handleDividerDoubleClick = useCallback(() => {
    resizeAllPanelsToDefault();
    handleDoubleClick();
  }, [handleDoubleClick, resizeAllPanelsToDefault]);

  useEffect(() => {
    const divider = dividerRef.current;

    if (!divider) {
      return;
    }

    return draggable({
      element: divider,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        disableNativeDragPreview({ nativeSetDragImage });
        preventUnhandled.start();
      },
      onDragStart() {
        enableTransitionsCallback(false);
        setIsDragging(true);
      },
      onDrag({ location }) {
        const leftNewWidth = getProposedPanelWidth({
          location,
          maxWidth: leftMaxWidth,
          minWidth: leftMinWidth,
          previousWidth: leftPanelPreviousWidth,
          isRighthandPanel: false,
        });

        const rightNewWidth = getProposedPanelWidth({
          location,
          maxWidth: rightMaxWidth,
          minWidth: rightMinWidth,
          previousWidth: rightPanelPreviousWidth,
          isRighthandPanel: true,
        });

        const shouldEnableTransitions =
          leftNewWidth === 0 || rightNewWidth === 0;

        enableTransitionsCallback(shouldEnableTransitions);

        handleSetWidthCallback(leftPanelName, leftNewWidth);

        handlePanelCollapseCallback(leftPanelName, leftNewWidth === 0);
        handlePanelCollapseCallback(rightPanelName, rightNewWidth === 0);

        if (rightPanelName === 'board') {
          handleSetWidthCallback(rightPanelName, rightNewWidth);
        }
      },
      onDrop({ location }) {
        preventUnhandled.stop();
        const leftNewWidth = getProposedPanelWidth({
          location,
          maxWidth: leftMaxWidth,
          minWidth: leftMinWidth,
          previousWidth: leftPreviousWidth,
          isRighthandPanel: false,
        });

        const rightNewWidth = getProposedPanelWidth({
          location,
          maxWidth: rightMaxWidth,
          minWidth: rightMinWidth,
          previousWidth: rightPreviousWidth,
          isRighthandPanel: true,
        });

        enableTransitionsCallback(true);

        if (leftNewWidth === 0) {
          collapsePanel(leftPanelName);
          handleSetWidthCallback(leftPanelName, leftPanelPreviousWidth);
        } else {
          setLeftPanelPreviousWidth(leftNewWidth);
          updateResizedPanelWidth(leftPanelName, leftNewWidth);
          handleSetWidthCallback(leftPanelName, leftNewWidth);
        }

        if (rightNewWidth === 0 && rightPanelName === 'board') {
          collapsePanel(rightPanelName);
          handleSetWidthCallback(rightPanelName, rightPanelPreviousWidth);
        } else if (rightPanelName === 'board') {
          setRightPanelPreviousWidth(rightNewWidth);
          updateResizedPanelWidth(rightPanelName, rightNewWidth);
          handleSetWidthCallback(rightPanelName, rightNewWidth);
        }

        setIsDragging(false);
      },
    });
  }, [
    collapsePanel,
    leftMaxWidth,
    leftMinWidth,
    leftPanelName,
    rightMaxWidth,
    rightMinWidth,
    rightPanelName,
    enableTransitionsCallback,
    leftPreviousWidth,
    rightPreviousWidth,
    handleSetWidthCallback,
    handlePanelCollapseCallback,
    leftPanelPreviousWidth,
    rightPanelPreviousWidth,
    updateResizedPanelWidth,
  ]);

  return (
    <div className={styles.dragAndDropDividerContainer}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions*/}
      <div
        className={cx(styles.dragAndDropDivider, {
          [styles.dragging]: isDragging,
          [styles.hidden]: shouldHideDivider,
        })}
        aria-label={intl.formatMessage({
          id: 'templates.split-screen-resize.drag-to-resize',
          defaultMessage: 'Drag to resize',
          description: 'Aria label for the resize divider',
        })}
        role="separator"
        ref={dividerRef}
        data-testid={getTestId<SplitScreenTestIds>('resize-divider')}
        onDoubleClick={handleDividerDoubleClick}
      />
    </div>
  );
};
